import React, { useEffect, useState, useMemo } from 'react';
import '../styles/LegalPage.css';

const termsSections = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'relationship', title: '1. Your Relationship' },
  { id: 'health', title: '2. Health & Safety' },
  { id: 'using', title: '3. Using InnerTap' },
  { id: 'premium', title: '4. Premium' },
  { id: 'privacy-terms', title: '5. Privacy' },
  { id: 'third-party', title: '6. Third-Party' },
  { id: 'availability', title: '7. Availability' },
  { id: 'disclaimers', title: '8. Disclaimers' },
  { id: 'disputes', title: '9. Disputes' },
  { id: 'general', title: '10. General' },
  { id: 'creator', title: '11. Creator Program' },
  { id: 'accessibility', title: '12. Accessibility' },
  { id: 'contact', title: '13. Contact' },
];

const privacySections = [
  { id: 'privacy-matters', title: 'Your Privacy Matters' },
  { id: 'commitments', title: 'Our Commitments' },
  { id: 'info-collect', title: '1. Information We Collect' },
  { id: 'how-use', title: '2. How We Use It' },
  { id: 'how-share', title: '3. How We Share It' },
  { id: 'your-rights', title: '4. Your Rights' },
  { id: 'california', title: '5. California Rights' },
  { id: 'european', title: '6. European Rights' },
  { id: 'security', title: '7. Data Security' },
  { id: 'retention', title: '8. Data Retention' },
  { id: 'international', title: '9. International Transfers' },
  { id: 'children', title: '10. Children' },
  { id: 'cookies', title: '11. Cookies' },
  { id: 'changes', title: '12. Changes' },
  { id: 'contact-privacy', title: '13. Contact' },
  { id: 'apple-watch', title: '14. Apple Watch' },
];

const faqSections = [
  { id: 'faq-coming-soon', title: 'FAQ Coming Soon' },
  { id: 'faq-updates', title: 'How to Stay Updated' },
  { id: 'faq-contact', title: 'Need Help?' },
];

const LegalPage = ({ initialTab = 'terms' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeSection, setActiveSection] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  const sections = useMemo(() => {
    if (activeTab === 'privacy') {
      return privacySections;
    }
    if (activeTab === 'faq') {
      return faqSections;
    }
    return termsSections;
  }, [activeTab]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setActiveSection('');
  }, [activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      const pageSections = Array.from(document.querySelectorAll('.legal-section'));
      const scrollPosition = window.scrollY + 150;

      pageSections.forEach((section) => {
        if (section.offsetTop <= scrollPosition && section.offsetTop + section.offsetHeight > scrollPosition) {
          setActiveSection(section.id);
        }
      });

      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="legal-page">
      <div className="legal-container">
        <nav className="nav-header">
          <div className="nav-content">
            <a href="/" className="logo-section">
              <div className="logo-icon app-icon-style">
                <img src="/innertap-logo.png" alt="InnerTap Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            </a>
            <div className="nav-links">
              <a
                href="#terms"
                className={activeTab === 'terms' ? 'active' : ''}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTab('terms');
                }}
              >
                Terms
              </a>
              <a
                href="#privacy"
                className={activeTab === 'privacy' ? 'active' : ''}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTab('privacy');
                }}
              >
                Privacy
              </a>
              <a
                href="#faq"
                className={activeTab === 'faq' ? 'active' : ''}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTab('faq');
                }}
              >
                FAQ
              </a>
              <a href="/" className="home-button">
                Back Home
              </a>
            </div>
          </div>
        </nav>

        <div className="content-wrapper">
          <aside className="sidebar">
            <h3>On this page</h3>
            <ul>
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={activeSection === section.id ? 'active' : ''}
                    onClick={(event) => {
                      event.preventDefault();
                      scrollToSection(section.id);
                    }}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <main className="main-content">
            {activeTab === 'terms' && <TermsContent />}
            {activeTab === 'privacy' && <PrivacyContent />}
            {activeTab === 'faq' && <FaqContent />}
          </main>
        </div>

        <div
          className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          role="button"
          aria-label="Scroll to top"
        >
          ↑
        </div>
      </div>
    </div>
  );
};

const TermsContent = () => (
  <>
    <div className="doc-header">
      <h1>Terms of Service</h1>
      <div className="last-updated">Last Updated: December 25, 2025</div>
    </div>

    <section id="welcome" className="legal-section">
      <h2>Welcome to InnerTap</h2>
      <p>We're glad you're here. These Terms of Service ("Terms") are the agreement between you and InnerTap, Inc. ("InnerTap," "we," "us," "our") that governs your use of the InnerTap mobile application and related services (collectively, the "Service").</p>
      <p>By using InnerTap, you're agreeing to these Terms. If you don't agree with them, please don't use our Service. We know legal documents can feel overwhelming, so we've written these Terms to be as clear as possible while still protecting both you and us.</p>
    </section>

    <section id="relationship" className="legal-section">
      <h2>1. Your Relationship with InnerTap</h2>

      <h3>1.1 Age Requirements</h3>
      <p>You must be at least 13 years old to use InnerTap. If you're between 13 and 17, you need your parent or guardian's permission and supervision to use our Service. By using InnerTap, you're confirming you meet these age requirements.</p>
      <p>We don't knowingly allow children under 13 to create accounts or use our Service. If we learn that someone under 13 has provided us with personal information, we'll delete it immediately.</p>

      <h3>1.2 Your InnerTap Account</h3>
      <p>Free users can use InnerTap anonymously if you prefer. InnerTap Premium requires creating an account, which helps protect your data and sync your progress across all your devices - so if you lose your phone or get a new device, your meditation journey is safe and waiting for you. If you choose to create an account:</p>
      <ul>
        <li>You're responsible for keeping your login credentials secure</li>
        <li>Everything that happens under your account is your responsibility</li>
        <li>You must provide accurate, current information</li>
        <li>One person per account - no sharing allowed</li>
        <li>You'll notify us immediately at support@innertap.com if you suspect unauthorized use</li>
      </ul>

      <h3>1.3 Account Security</h3>
      <p>You may use your InnerTap account on multiple personal devices. To protect against unauthorized sharing, we monitor for unusual activity patterns. Using your account on many devices simultaneously may trigger security review.</p>
    </section>

    <section id="health" className="legal-section">
      <h2>2. Health and Safety Disclaimers</h2>

      <h3>2.1 InnerTap Is Not Medical Care</h3>
      <div className="warning-box">
        <p><strong>This is important:</strong> InnerTap provides mindfulness and meditation content for general wellness purposes only. We are not:</p>
        <ul>
          <li>Medical or healthcare providers</li>
          <li>Mental health professionals</li>
          <li>A substitute for professional medical or psychological care</li>
          <li>A treatment for any physical or mental health condition</li>
        </ul>
      </div>
      <p>Never disregard professional medical advice or delay seeking it because of something you experience in InnerTap. If you have health concerns, please consult qualified healthcare providers.</p>

      <h3>2.2 Emergency Situations</h3>
      <div className="warning-box">
        <p><strong>InnerTap cannot help in emergencies.</strong> If you're experiencing a medical emergency or mental health crisis:</p>
        <ul>
          <li>Call 911 (or your local emergency number) immediately</li>
          <li>For thoughts of self-harm or suicide, contact the 988 Suicide &amp; Crisis Lifeline (US)</li>
          <li>Do not rely on InnerTap for emergency or urgent care</li>
        </ul>
      </div>

      <h3>2.3 Safe Use Guidelines</h3>
      <p>Please use InnerTap safely and responsibly:</p>
      <ul>
        <li><strong>Never use while driving, operating machinery, or in any situation where distraction could cause harm to yourself or others</strong></li>
        <li><strong>For movement-based practices (walking meditation, mindful movement, etc.):</strong> Choose a safe, controlled environment free from traffic, obstacles, stairs, or hazards. Remain aware of your surroundings at all times. While these practices are designed to keep your eyes open, if you choose to close your eyes during any session, ensure you are stationary and in a completely safe space. Limit how often you look at your screen - glance briefly when needed rather than continuous viewing while moving.</li>
        <li><strong>Listen to your body:</strong> Movement practices should feel comfortable and safe. Only do what feels right for your body and current physical condition. Stop immediately if you feel unsteady, dizzy, or uncomfortable.</li>
        <li>Only use seated or lying meditations in a safe, comfortable environment where you can fully relax without danger</li>
        <li>Some sessions involve closing your eyes or deep relaxation - ensure you're in an appropriate setting</li>
        <li>Stop immediately if you feel distressed, dizzy, or uncomfortable and seek help if needed</li>
      </ul>

      <h3>2.4 Individual Results Vary</h3>
      <p>While many people find meditation beneficial, we cannot guarantee specific outcomes. Everyone's experience is different. A small percentage of people with certain conditions may experience discomfort during meditation. If you have concerns about whether meditation is right for you, please consult your healthcare provider first.</p>

      <h3>2.5 Audio Safety and Hearing Protection</h3>
      <div className="warning-box">
        <p><strong>Protect your hearing:</strong> InnerTap uses audio content including meditation narration, music, and singing bowls. To use our Service safely:</p>
        <ul>
          <li><strong>Always start at a low volume</strong> before beginning any session and adjust gradually to a comfortable level</li>
          <li>You are responsible for controlling your device's volume settings at all times</li>
          <li>If you experience any discomfort, ringing, or pain in your ears, stop immediately and lower your volume</li>
          <li>Never listen at volumes that prevent you from hearing your surroundings when awareness is needed</li>
        </ul>
      </div>

      <p><strong>Safe Listening Practices:</strong></p>
      <p>To protect your hearing during meditation sessions:</p>
      <ul>
        <li>Use lower volume settings when listening through headphones or earbuds compared to speakers</li>
        <li>Take breaks from audio exposure, especially during extended listening sessions</li>
        <li>Be mindful that different sessions and sounds may play at varying volume levels - always check your volume before starting</li>
        <li>Our singing bowl sounds and certain soundscapes can be particularly resonant - test these at low volumes first</li>
        <li>If you plan to meditate for extended periods, keep your volume at conservative levels to prevent cumulative exposure</li>
      </ul>

      <p><strong>Sound Therapy as Complementary Wellness:</strong></p>
      <p>InnerTap's audio features, including singing bowls and soundscapes, are designed for relaxation and general wellness purposes only. They are not medical devices and are not intended to diagnose, treat, cure, or prevent any medical condition. Sound therapy should be considered a complementary practice, not a replacement for professional medical advice or treatment.</p>

      <p><strong>Special Considerations:</strong></p>
      <p>If you have existing hearing conditions, wear hearing aids, have metal implants (such as pacemakers), are pregnant, or have any medical concerns about sound exposure, please consult your healthcare provider before using InnerTap's audio features.</p>

      <p><strong>Your Responsibility and Our Limitations:</strong></p>
      <p>You acknowledge and accept sole responsibility for setting appropriate volume levels and monitoring your listening duration. InnerTap is not liable for any hearing-related issues, discomfort, or damage that may result from your use of our audio features. Your use of all sound and audio features is entirely at your own risk. By using InnerTap, you agree that you will not hold InnerTap liable for any injuries, losses, or damages related to audio exposure or volume levels while using our Service.</p>
    </section>

    <section id="using" className="legal-section">
      <h2>3. Using InnerTap</h2>

      <h3>3.1 Your License to Use Our Service</h3>
      <p>We grant you a personal, non-exclusive, non-transferable, revocable license to use InnerTap for your personal, non-commercial purposes. This means you can use and enjoy InnerTap for yourself, but you don't own any part of our Service or content.</p>

      <h3>3.2 No Account Sharing</h3>
      <p>Your account is for your personal use only. Sharing login credentials with others, including family members, violates these Terms and may result in account termination. Each person needs their own account.</p>

      <h3>3.3 Acceptable Use</h3>
      <p>You agree to use InnerTap responsibly and legally. You will not:</p>

      <p><strong>Misuse Our Content:</strong></p>
      <ul>
        <li>Copy, download, or record our meditation sessions (except for temporary streaming within the app)</li>
        <li>Distribute or publicly perform our content without permission</li>
        <li>Use our content for commercial purposes or in public classes without a license</li>
        <li>Create derivative works from our meditations or features</li>
      </ul>

      <p><strong>Compromise Security or Technology:</strong></p>
      <ul>
        <li>Attempt to hack, probe, or breach our security</li>
        <li>Reverse engineer or decompile our app</li>
        <li>Use bots, scripts, or automated means to access our Service</li>
        <li>Circumvent subscription paywalls or other technological measures</li>
        <li>Install or use modified or pirated versions of our app</li>
      </ul>

      <p><strong>Engage in Harmful Behavior:</strong></p>
      <ul>
        <li>Use InnerTap for any illegal purpose</li>
        <li>Violate any laws, regulations, or third-party rights</li>
        <li>Transmit malicious code or interfere with our Service</li>
        <li>Impersonate anyone or provide false information</li>
        <li>Harass or abuse our support team</li>
      </ul>

      <h3>3.4 Intellectual Property</h3>
      <p>Everything in InnerTap - the meditations, music, visuals, text, logos, and code - belongs to us or our licensors and is protected by intellectual property laws. Your use of InnerTap doesn't give you ownership of any content.</p>
      <p>The InnerTap name and logo are our trademarks. Please don't use them without our written permission.</p>

      <h3>3.5 Feedback and Suggestions</h3>
      <p>We value your feedback about InnerTap - it helps us improve and grow. When you submit feedback, suggestions, or ideas to us (through any channel), you grant InnerTap a worldwide, perpetual, irrevocable, royalty-free license to use, modify, create derivative works from, and incorporate your feedback into our services for any purpose without compensation or attribution to you.</p>
      <p>We read all feedback carefully, though we can't respond to everyone or implement every suggestion. Please don't share anything you consider proprietary or confidential.</p>

      <h3>3.6 Reviews and Testimonials</h3>
      <p>Your reviews help others discover InnerTap and join our mindfulness community - we're genuinely grateful for them. When you post a public review about InnerTap on any platform (App Store, Google Play, social media, etc.), you grant us permission to:</p>
      <ul>
        <li>Quote or display your review in our marketing materials, website, and app</li>
        <li>Share it on our social media channels</li>
        <li>Edit for length or clarity while maintaining your intended meaning</li>
        <li>Use it in any promotional context</li>
      </ul>
      <p>We'll attribute reviews to your publicly displayed name (or first name and last initial) unless you request otherwise. If you'd like your review removed from our promotional use, email support@innertap.com and we'll honor your request within 30 days.</p>

      <h3>3.7 Copyright Infringement Claims</h3>
      <p>If you believe content in InnerTap infringes your copyright, notify us:</p>
      <div className="highlight-box">
        <p><strong>Email:</strong> support@innertap.com<br />
        <strong>Subject:</strong> "DMCA Notice"</p>
        <p>Include:</p>
        <ul>
          <li>Identification of the copyrighted work</li>
          <li>Description of the allegedly infringing content</li>
          <li>Your contact information</li>
          <li>A statement of good faith belief</li>
          <li>A statement of accuracy under penalty of perjury</li>
          <li>Your physical or electronic signature</li>
        </ul>
      </div>
      <p>We'll investigate and may remove content if appropriate.</p>

      <h3>3.8 Rights in Your Content</h3>
      <p>You retain ownership of any original content you create. By sharing it with us or posting it publicly about InnerTap, you grant us the license to use it as described above. This license continues even if you stop using InnerTap, though you can always request removal of testimonials as noted.</p>
      <p>We're not obligated to use any feedback or reviews, and anything you submit may be used without notifying you first (except we'll never materially change the meaning of your reviews).</p>
    </section>

    <section id="premium" className="legal-section">
      <h2>4. InnerTap Premium</h2>

      <h3>4.1 Subscription Options</h3>
      <p>InnerTap offers free features and an optional premium subscription ("InnerTap Premium") with additional content like exclusive voices and extended soundscapes. Premium membership requires creating an account to protect your data, sync your progress across devices, and ensure you never lose your meditation history if you switch phones or devices.</p>

      <h3>4.2 Billing and Auto-Renewal</h3>
      <p><strong>Subscriptions automatically renew</strong> unless canceled at least 24 hours before the end of the current billing period. Your account will be charged for renewal within 24 hours prior to the end of the current period. You can manage and cancel your subscription in your device's account settings.</p>

      <h3>4.3 Free Trials</h3>
      <p>If we offer you a free trial:</p>
      <ul>
        <li>It begins when you activate it</li>
        <li>It automatically converts to a paid subscription when the trial ends</li>
        <li>You can cancel anytime during the trial to avoid charges</li>
        <li>You're only eligible for one free trial per account</li>
        <li>Any unused portion of a free trial will be forfeited when you purchase a subscription</li>
      </ul>
      <p>To ensure trial fairness, we track trial usage at the device level through our subscription service provider.</p>

      <h3>4.4 Cancellation</h3>
      <p>You can cancel anytime through your app store subscription settings:</p>
      <ul>
        <li><strong>For Apple App Store:</strong> Go to Settings &gt; [Your Name] &gt; Subscriptions</li>
        <li><strong>For Google Play:</strong> Go to Play Store &gt; Menu &gt; Subscriptions</li>
      </ul>
      <p>Cancellation takes effect at your next renewal date. You'll keep Premium access through the end of your current paid period.</p>

      <h3>4.4.1 Account Deletion vs Subscription Cancellation</h3>
      <div className="warning-box">
        <p><strong>Important - these are two separate actions:</strong></p>
        <ul>
          <li>Deleting your InnerTap account removes your data and profile</li>
          <li>Canceling your subscription stops future billing</li>
          <li>One does not automatically do the other</li>
        </ul>
      </div>

      <p>If you want to stop using InnerTap Premium and stop being charged:</p>
      <ul>
        <li>First, cancel your subscription through your App Store or Google Play settings (see Section 4.4 above)</li>
        <li>Then, if you also want to delete your data, you can delete your account</li>
        <li>Deleting your account without canceling your subscription will result in continued billing even though you can no longer access the Service</li>
      </ul>

      <p>We know this seems like an extra step, but it's because subscriptions are managed by Apple and Google, not by us directly. This protects your payment information and gives you control through platforms you already trust.</p>

      <h3>4.5 Refunds</h3>
      <p>All payments are final and non-refundable. We cannot issue refunds for partial periods, but you'll retain access through the end of any period you've paid for. Refund requests must go through the app store where you purchased.</p>

      <h3>4.6 Price Changes</h3>
      <p>We may adjust prices for new subscribers, but if you maintain continuous subscription, you'll keep your original price forever. This is our commitment to early supporters. If you cancel and resubscribe later, you'll pay current prices.</p>

      <h3>4.7 Payment Issues and Grace Periods</h3>
      <p>If your payment fails (expired card, insufficient funds, etc.), we want to keep you in your meditation practice:</p>
      <ul>
        <li>Apple and Google may provide a grace period (typically 6-16 days) to update your payment information</li>
        <li>During this grace period, you maintain access to Premium features</li>
        <li>You'll receive notifications to update your payment method</li>
        <li>If payment isn't resolved by the end of the grace period, your Premium access will be suspended</li>
        <li>Once you update payment, your subscription continues from where it left off - no lost time</li>
      </ul>
      <p><strong>Important:</strong> The grace period is controlled by the app stores, not InnerTap. Different platforms may have different grace period lengths. Your original subscription date and grandfathered pricing (if applicable) are preserved as long as you resolve payment within the grace period.</p>

      <h3>4.8 Gift Subscriptions</h3>
      <p>InnerTap gifts are one-time purchases providing Premium access for a fixed period (3 months or 1 year).</p>
      <ul>
        <li>Purchased as single payment, not recurring</li>
        <li>Non-refundable once gift code is delivered</li>
        <li>Gift codes expire 24 months after purchase</li>
        <li>Cannot be combined with trials or other promotions</li>
        <li>After gift period ends, recipient can subscribe at current rates</li>
      </ul>

      <h3>4.9 Promotional Offers</h3>
      <p>We occasionally offer promotional codes, discounts, or special pricing.</p>
      <p><strong>Promotional Terms:</strong></p>
      <ul>
        <li>One promotional offer per account unless stated otherwise</li>
        <li>Cannot be combined with other offers or trials</li>
        <li>May have expiration dates or limited quantities</li>
        <li>Non-transferable and have no cash value</li>
        <li>We reserve the right to revoke promotions obtained fraudulently</li>
        <li>Promotional pricing may not be available in all regions</li>
        <li>Student/military/healthcare discounts may require verification</li>
      </ul>

      <h3>4.10 Taxes</h3>
      <p>Subscription prices may not include applicable taxes (VAT, GST, sales tax). You're responsible for any taxes owed based on your location. App stores typically handle tax collection and remittance for digital goods.</p>
    </section>

    <section id="privacy-terms" className="legal-section">
      <h2>5. Privacy and Data</h2>
      <p>Your privacy matters deeply to us. Our Privacy Policy (available at innertap.com/privacy) explains how we collect, use, and protect your information. Key points:</p>
      <ul>
        <li>We collect only what we need to provide and improve our Service</li>
        <li>We never sell your personal data</li>
        <li>Free users can use InnerTap anonymously; Premium requires an account to protect and sync your data</li>
        <li>You can delete your account and data anytime</li>
      </ul>
    </section>

    <section id="third-party" className="legal-section">
      <h2>6. Third-Party Services</h2>
      <p>We use trusted third-party services to operate InnerTap:</p>
      <ul>
        <li>Firebase (Google) for infrastructure and analytics</li>
        <li>RevenueCat for subscription management</li>
        <li>App stores for payment processing</li>
      </ul>
      <p>When you use InnerTap through Apple App Store or Google Play, you're also subject to their terms. These platforms handle all payment processing - we don't have access to your payment details.</p>
    </section>

    <section id="availability" className="legal-section">
      <h2>7. Service Availability and Updates</h2>

      <h3>7.1 Service "As Is"</h3>
      <p>We work hard to keep InnerTap available and running smoothly, but we can't guarantee perfect uptime or performance. The Service is provided "as is" and "as available."</p>

      <h3>7.2 Updates and Changes</h3>
      <p>We may update InnerTap periodically to add features, fix issues, or improve performance. Some updates may install automatically. We may also modify or discontinue features with or without notice.</p>

      <h3>7.3 Technical Requirements</h3>
      <p>InnerTap requires:</p>
      <ul>
        <li>A compatible mobile device with a supported operating system</li>
        <li>An internet connection for most features</li>
        <li>Sufficient storage space for any offline content</li>
      </ul>
      <p>You're responsible for any data charges from your mobile carrier.</p>

      <h3>7.4 Beta Features</h3>
      <p>From time to time, we may offer early access to new features ("Beta Features") that are still in development.</p>
      <p>Beta Features are:</p>
      <ul>
        <li>Provided "as is" without warranties of any kind</li>
        <li>May contain bugs or errors</li>
        <li>Subject to change or discontinuation without notice</li>
        <li>Not guaranteed to become permanent features</li>
        <li>Subject to additional terms presented when you access them</li>
      </ul>
      <p>By using Beta Features, you acknowledge these limitations and agree to provide feedback if requested. Your use of Beta Features is at your own risk, and Section 8 (Disclaimers and Limitations) applies with particular force to Beta Features.</p>

      <h3>7.5 Content Availability and Third-Party Licenses</h3>
      <p>The meditation sessions, soundscapes, music, and voice recordings in InnerTap may include content we license from third parties. We work hard to maintain a consistent library, but licensing agreements can change.</p>
      <p>We reserve the right to:</p>
      <ul>
        <li>Modify, replace, or remove any content to comply with licensing requirements</li>
        <li>Substitute content with similar alternatives if licenses expire</li>
        <li>Add or remove voice options, music tracks, or soundscapes</li>
        <li>Adjust content availability by region due to licensing restrictions</li>
      </ul>
      <p>Our guided meditations use various voice technologies to provide diverse, high-quality narration options. This may include professional voice actors, voice synthesis technology, and audio processing enhancements. We select each voice based on its effectiveness for meditation, not its origin.</p>
      <p>What this means for you:</p>
      <ul>
        <li>Your favorite meditation might occasionally be updated or replaced</li>
        <li>Downloaded content may expire and require re-downloading</li>
        <li>Some content may not be available in all countries</li>
      </ul>
      <p>We'll do our best to:</p>
      <ul>
        <li>Provide advance notice of significant content changes when possible</li>
        <li>Replace removed content with similar quality alternatives</li>
        <li>Maintain the core functionality you rely on for your practice</li>
      </ul>
      <p>You acknowledge that we cannot guarantee perpetual availability of specific content, and we're not liable for any changes to our content library. Your subscription provides access to our available library at any given time, not to specific pieces of content.</p>

      <h3>7.6 Apple Watch Companion App</h3>
      <p>InnerTap offers an optional Apple Watch companion app that extends your meditation experience to your wrist. The Watch app requires:</p>
      <ul>
        <li>An iPhone with InnerTap installed</li>
        <li>Apple Watch Series 4 or later</li>
        <li>watchOS 10 or later</li>
        <li>Bluetooth connection between devices</li>
      </ul>
      <p>The Watch app provides supplementary functionality and is not required to use InnerTap's core features. Some Watch features (Phone Sync mode) require an active connection to your iPhone, while others (Singing Bowl, Haptic Press) work independently on your Watch.</p>
      <p>Apple Watch features may vary based on your device model, watchOS version, and connectivity status. We reserve the right to modify Watch app features in future updates.</p>
    </section>

    <section id="disclaimers" className="legal-section">
      <h2>8. Disclaimers and Limitations</h2>

      <h3>8.1 No Warranties</h3>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, INNERTAP AND ITS AFFILIATES, SUPPLIERS, AND PARTNERS DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DON'T GUARANTEE THAT INNERTAP WILL MEET YOUR REQUIREMENTS, BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE.</p>

      <h3>8.2 Limitation of Liability</h3>
      <p>TO THE FULLEST EXTENT PERMITTED BY LAW:</p>
      <ul>
        <li>INNERTAP'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS WILL NOT EXCEED THE GREATER OF $50 OR THE AMOUNT YOU'VE PAID US IN THE 12 MONTHS BEFORE THE CLAIM</li>
        <li>WE'RE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA, USE, OR GOODWILL</li>
      </ul>
      <p>Some jurisdictions don't allow these limitations, so they may not apply to you.</p>

      <h3>8.3 Indemnification</h3>
      <p>You agree to defend, indemnify, and hold harmless InnerTap and its affiliates from any third-party claims, losses, or damages arising from:</p>
      <ul>
        <li>Your use of InnerTap in violation of these Terms</li>
        <li>Your breach of any law or third-party rights</li>
        <li>Any content you submit through our Service</li>
      </ul>
    </section>

    <section id="disputes" className="legal-section">
      <h2>9. Dispute Resolution</h2>

      <h3>9.1 Governing Law</h3>
      <p>These Terms are governed by the laws of the State of Florida, USA, without regard to conflict of law principles.</p>

      <h3>9.2 Arbitration Agreement (US Users)</h3>
      <div className="highlight-box">
        <p><strong>Please read carefully - this affects your legal rights.</strong></p>
      </div>
      <p>If you're in the United States, you and InnerTap agree to resolve disputes through binding arbitration instead of court, except as described below. This means you waive your right to a jury trial and class actions.</p>
      <p><strong>Your Right to Opt Out:</strong> You can opt out of arbitration by emailing us at support@innertap.com within 30 days of first using InnerTap. Include "Arbitration Opt-Out" in the subject line and your name and account information.</p>
      <p><strong>How Arbitration Works:</strong></p>
      <ul>
        <li>Arbitration will be conducted by the American Arbitration Association under its Consumer Arbitration Rules</li>
        <li>The arbitrator's decision is final and enforceable in court</li>
        <li>Arbitration will be individual - no class arbitrations allowed</li>
        <li>We'll cover reasonable arbitration fees for non-frivolous claims</li>
      </ul>
      <p><strong>Exceptions:</strong> You or we may bring claims in small claims court if they qualify. Either party may seek injunctive relief in court for intellectual property disputes.</p>

      <h3>9.3 International Users</h3>
      <p>If you're outside the US, you may have the right to resolve disputes in your local courts according to your local laws.</p>

      <h3>9.4 Time Limit for Claims</h3>
      <p>Any claim arising from your use of InnerTap must be filed within one year after it arose, or it's permanently barred.</p>
    </section>

    <section id="general" className="legal-section">
      <h2>10. General Terms</h2>

      <h3>10.1 Termination</h3>
      <p>You can stop using InnerTap anytime. We may suspend or terminate your access if you violate these Terms or for other legitimate business reasons. Upon termination, your license to use InnerTap ends immediately. Please note: Terminating your account does not automatically cancel any active Premium subscription - you'll need to cancel through your App Store or Google Play settings to stop recurring charges (see Section 4.4 for instructions).</p>

      <h3>10.2 Changes to These Terms</h3>
      <p>We may update these Terms occasionally. We'll notify you of significant changes through the app or email. Continued use after changes means you accept the new Terms.</p>

      <h3>10.3 Entire Agreement</h3>
      <p>These Terms and our Privacy Policy constitute the entire agreement between you and InnerTap regarding the Service.</p>

      <h3>10.4 Severability</h3>
      <p>If any part of these Terms is found unenforceable, the rest remains in effect.</p>

      <h3>10.5 No Waiver</h3>
      <p>Our failure to enforce any right or provision isn't a waiver of that right or provision.</p>

      <h3>10.6 Assignment</h3>
      <p>You can't transfer these Terms or your rights under them. We may assign our rights to any successor in interest.</p>

      <h3>10.7 Electronic Communications</h3>
      <p>By using InnerTap, you consent to receiving electronic communications from us. These electronic communications satisfy any legal requirements for written communication.</p>
    </section>

    <section id="creator" className="legal-section">
      <h2>11. Creator and Affiliate Program</h2>

      <h3>11.1 Joining Our Community of Creators</h3>
      <p>If you're interested in sharing InnerTap with others through our Creator Program, welcome! This program lets approved creators earn commissions while helping others discover mindfulness. By participating, you agree to these additional terms.</p>

      <h3>11.2 Promoting with Integrity</h3>
      <p>We trust our creators to represent InnerTap authentically. As a creator, you agree to:</p>
      <ul>
        <li>Share honest experiences and accurate information about InnerTap</li>
        <li>Clearly disclose your partnership using #ad, "InnerTap affiliate," or similar clear language that follows FTC guidelines</li>
        <li>Respect your audience by avoiding spam, misleading claims, or aggressive marketing</li>
        <li>Not make medical claims or promise specific health outcomes</li>
        <li>Not target vulnerable populations or use manipulative tactics</li>
      </ul>

      <h3>11.3 Protecting Our Community</h3>
      <p>To maintain trust, creators cannot:</p>
      <ul>
        <li>Use paid ads with affiliate links without our written approval</li>
        <li>Create misleading websites or social accounts that impersonate InnerTap</li>
        <li>Bid on "InnerTap" or related keywords in paid search</li>
        <li>Promote InnerTap alongside inappropriate or harmful content</li>
      </ul>

      <h3>11.4 Commission Structure</h3>
      <p>We value your efforts to grow our community:</p>
      <ul>
        <li>Commissions are earned on completed purchases through your unique link</li>
        <li>Rates and terms are detailed in your creator agreement</li>
        <li>We'll notify you 30 days in advance of any rate changes</li>
        <li>Commissions may be reversed only for refunds or fraud</li>
      </ul>

      <h3>11.5 Growing Together</h3>
      <p>Our Creator Program evolves as we learn what works best for our community. We may update program terms with reasonable notice. If a creator's promotional style doesn't align with our values or violates these terms, we may end the partnership. Any earned commissions up to that point will be honored unless terminated for policy violations involving dishonesty or harm.</p>
    </section>

    <section id="accessibility" className="legal-section">
      <h2>12. Accessibility</h2>
      <p>InnerTap strives to be accessible to all users. If you experience accessibility issues:</p>
      <ul>
        <li>Contact support@innertap.com with "Accessibility" in the subject</li>
        <li>We'll work to provide content in alternative formats when possible</li>
        <li>We welcome feedback on improving accessibility</li>
      </ul>
    </section>

    <section id="contact" className="legal-section">
      <h2>13. Contact Us</h2>
      <p>Questions about these Terms? We're here to help:</p>
      <div className="highlight-box">
        <p><strong>Email:</strong> support@innertap.com<br />
        <strong>Mail:</strong> InnerTap, Inc.<br />
        Newberry, FL 32669<br />
        United States</p>
      </div>
    </section>
  </>
);

const PrivacyContent = () => (
  <>
    <div className="doc-header">
      <h1>Privacy Policy</h1>
      <div className="last-updated">Last Updated: December 25, 2025</div>
    </div>

    <section id="privacy-matters" className="legal-section">
      <h2>Your Privacy Matters</h2>
      <p>At InnerTap, we believe mindfulness includes being mindful about privacy. This Privacy Policy explains how we handle your information when you use InnerTap. We've written it to be clear and comprehensive because transparency builds trust.</p>
    </section>

    <section id="commitments" className="legal-section">
      <h2>Our Privacy Commitments</h2>
      <div className="highlight-box">
        <p>Before diving into details, here are our core promises:</p>
        <ul>
          <li>We collect only what's necessary to provide and improve InnerTap</li>
          <li>We never sell your personal data to anyone</li>
          <li>Free users can use InnerTap anonymously; Premium subscribers create accounts to protect and sync their data</li>
          <li>You control your data - delete it anytime</li>
          <li>We use industry-standard security to protect your information</li>
        </ul>
      </div>
    </section>

    <section id="info-collect" className="legal-section">
      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <p><strong>Account Information</strong></p>
      <p>If you create an account (required for Premium to protect your data; optional for free users), we collect:</p>
      <ul>
        <li>Email address</li>
        <li>Name (if you provide it)</li>
        <li>Basic profile information from Google or Apple if you use their sign-in</li>
      </ul>

      <p><strong>Support Communications</strong></p>
      <p>When you contact us for help, we collect:</p>
      <ul>
        <li>Your email address</li>
        <li>The content of your message</li>
        <li>Any information you choose to share</li>
      </ul>

      <p><strong>Email Communications</strong></p>
      <p>When you sign up for marketing emails or receive transactional notifications, we collect:</p>
      <ul>
        <li>Email message content (for customer support history)</li>
        <li>Communication preferences</li>
        <li>Delivery and engagement data (whether emails were delivered and opened)</li>
      </ul>

      <h3>1.2 Information Collected Automatically</h3>
      <p><strong>App Usage Data</strong></p>
      <p>We collect information about how you use InnerTap:</p>
      <ul>
        <li>Which meditations you play</li>
        <li>Session duration and frequency</li>
        <li>Features you use</li>
        <li>Your meditation streak and progress</li>
      </ul>
      <p>If you're not logged in (free users only), this data is anonymous. If you have an account, we associate it with your profile to sync across devices and preserve your progress.</p>

      <p><strong>Device Information</strong></p>
      <p>We collect basic device data:</p>
      <ul>
        <li>Device model and operating system</li>
        <li>App version</li>
        <li>General location (country/region from IP address)</li>
        <li>Device identifiers provided by your operating system. On iOS, we may request access to your device's Advertising Identifier (IDFA) through Apple's App Tracking Transparency framework. You can choose to allow or deny this request. If you deny, we will not track you across other companies' apps or websites. You can change this setting anytime in Settings &gt; Privacy &amp; Security &gt; Tracking.</li>
      </ul>

      <p><strong>Diagnostic Data</strong></p>
      <p>To improve app stability and performance, we automatically collect:</p>
      <ul>
        <li>Crash reports (app state at time of crash)</li>
        <li>Performance metrics (app launch time, response times)</li>
      </ul>
      <p>This data is linked to your account if you're logged in, helping us identify and fix issues affecting your experience.</p>

      <p><strong>Purchase Information</strong></p>
      <p>If you subscribe to InnerTap Premium, we collect:</p>
      <ul>
        <li>Subscription status and type</li>
        <li>Purchase history and transaction identifiers</li>
        <li>Trial eligibility status</li>
      </ul>
      <p>This information is managed through RevenueCat and the app store where you purchased.</p>

      <p><strong>Cookies and Similar Technologies</strong></p>
      <p>We use cookies and similar technologies on our website to:</p>
      <ul>
        <li>Remember your preferences</li>
        <li>Understand how visitors use our site</li>
        <li>Measure the effectiveness of our campaigns</li>
      </ul>
    </section>

    <section id="how-use" className="legal-section">
      <h2>2. How We Use It</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide and personalize the InnerTap experience</li>
        <li>Maintain and improve the Service</li>
        <li>Communicate with you about updates, offers, and support requests</li>
        <li>Monitor analytics to understand how InnerTap is used</li>
        <li>Protect against fraudulent or unauthorized activity</li>
      </ul>
      <p>We may send you meditation tips, product updates, or offers. You can opt out of marketing emails at any time by clicking "unsubscribe" or contacting us.</p>
    </section>

    <section id="how-share" className="legal-section">
      <h2>3. How We Share It</h2>
      <p>We only share your information when necessary to operate InnerTap or when required by law. This includes sharing with:</p>
      <ul>
        <li>Service providers we trust (like Firebase and RevenueCat)</li>
        <li>App stores that process payments</li>
        <li>Analytics partners that help us improve InnerTap</li>
      </ul>
      <p>We require these partners to protect your information and use it only for our purposes.</p>
      <p>We do not use your personal data to train artificial intelligence or machine learning models. Our third-party partners (Firebase, RevenueCat) are contractually prohibited from using your data for AI training purposes.</p>
    </section>

    <section id="your-rights" className="legal-section">
      <h2>4. Your Rights</h2>
      <p>Depending on where you live, you may have rights to:</p>
      <ul>
        <li>Access the data we hold about you</li>
        <li>Request corrections or updates</li>
        <li>Delete your account and associated data</li>
        <li>Export your data in a portable format</li>
        <li>Opt out of certain data uses</li>
      </ul>
      <p>You can make these requests by emailing support@innertap.com. We'll respond within 30 days.</p>
    </section>

    <section id="california" className="legal-section">
      <h2>5. California Rights</h2>
      <p>California residents have specific rights under the California Consumer Privacy Act (CCPA), including:</p>
      <ul>
        <li>The right to know what personal information we collect</li>
        <li>The right to request deletion of their information</li>
        <li>The right to opt out of "sale" of their information (we don't sell personal data)</li>
      </ul>
      <p>To exercise these rights, contact us at support@innertap.com. We'll verify your identity before processing requests.</p>
    </section>

    <section id="european" className="legal-section">
      <h2>6. European Rights</h2>
      <p>If you're in the European Union or United Kingdom, the General Data Protection Regulation (GDPR) gives you rights to:</p>
      <ul>
        <li>Access and correct your data</li>
        <li>Object to certain processing</li>
        <li>Request deletion or restriction of processing</li>
        <li>Request data portability</li>
        <li>Lodge a complaint with your data protection authority</li>
      </ul>
      <p>Our legal basis for collecting and using personal data depends on the activity. It may be based on your consent, our contract with you, or our legitimate interests in improving InnerTap.</p>
    </section>

    <section id="security" className="legal-section">
      <h2>7. Data Security</h2>
      <p>We use technical and organizational measures to protect your data, including encryption, access controls, and monitoring. While no system is perfectly secure, we work hard to safeguard your information.</p>
    </section>

    <section id="retention" className="legal-section">
      <h2>8. Data Retention</h2>
      <p>We keep your information only as long as needed to provide InnerTap or comply with legal obligations. If you delete your account, we'll remove your personal data within 30 days unless we need to retain it for legal reasons.</p>
    </section>

    <section id="international" className="legal-section">
      <h2>9. International Transfers</h2>
      <p>InnerTap is based in the United States. If you access our Service from outside the US, your information may be transferred, stored, and processed here. We use standard contractual clauses and other safeguards for international transfers.</p>
    </section>

    <section id="children" className="legal-section">
      <h2>10. Children</h2>
      <p>InnerTap isn't intended for children under 13. We don't knowingly collect data from children. If we learn that a child has provided us information, we'll delete it promptly. Parents who believe their child provided information can contact us for assistance.</p>
    </section>

    <section id="cookies" className="legal-section">
      <h2>11. Cookies</h2>
      <p>We use cookies and similar technologies on our website. You can control cookies through your browser settings. Disabling cookies may impact certain features.</p>
    </section>

    <section id="changes" className="legal-section">
      <h2>12. Changes</h2>
      <p>We may update this Privacy Policy. We'll notify you about significant changes through the app or email. The "Last Updated" date reflects the most recent version.</p>
    </section>

    <section id="contact-privacy" className="legal-section">
      <h2>13. Contact</h2>
      <p>Questions about your privacy? Email us at support@innertap.com. We're here to help.</p>
    </section>

    <section id="apple-watch" className="legal-section">
      <h2>14. Apple Watch Companion App</h2>
      <p>InnerTap offers an optional Apple Watch companion app. Here's how we handle data on your Watch:</p>

      <h3>14.1 Data Synchronized to Apple Watch</h3>
      <p>When you use the Watch app, your iPhone sends the following to your Watch:</p>
      <ul>
        <li>Affirmation text (displayed when you tap)</li>
        <li>Background images you select (compressed for watch display)</li>
        <li>Your session mode preference</li>
      </ul>

      <h3>14.2 Data Sent from Apple Watch</h3>
      <p>Your Watch sends minimal data back to your iPhone:</p>
      <ul>
        <li>Tap events (timestamp only) to trigger affirmation playback on your iPhone</li>
      </ul>

      <h3>14.3 How Data is Transmitted</h3>
      <p>All communication between your iPhone and Apple Watch uses Apple's WatchConnectivity framework, which is encrypted by Apple. The Watch app does not make any direct internet connections - all network activity goes through your paired iPhone.</p>

      <h3>14.4 Local Watch Storage</h3>
      <p>The Watch app stores locally on your device:</p>
      <ul>
        <li>Your session mode preference</li>
        <li>Background image (stored in the Watch app's private storage)</li>
      </ul>
      <p>This data remains on your Watch until you uninstall the Watch app, reset your Apple Watch, or select a different background image.</p>

      <h3>14.5 Health and Fitness Data</h3>
      <div className="highlight-box">
        <p><strong>Important:</strong> The InnerTap Apple Watch app does NOT access HealthKit or collect any health, fitness, or biometric data. We do not read your heart rate, activity data, workout information, or any health metrics from your Apple Watch.</p>
      </div>

      <h3>14.6 Audio and Haptics</h3>
      <p>The Watch app plays locally bundled audio files (singing bowl sounds) and provides haptic feedback for meditation sessions. No audio is recorded or transmitted from your Watch.</p>
    </section>
  </>
);

const FaqContent = () => (
  <>
    <div className="doc-header">
      <h1>Frequently Asked Questions</h1>
      <div className="last-updated">Last Updated: January 15, 2025</div>
    </div>

    <section id="faq-coming-soon" className="legal-section">
      <h2>FAQ Coming Soon</h2>
      <p>Our team is gathering the most helpful questions from the InnerTap community. We'll add detailed answers about subscriptions, features, and mindful best practices soon. Check back after our public launch.</p>
    </section>

    <section id="faq-updates" className="legal-section">
      <h2>How to Stay Updated</h2>
      <p>Want to know when the FAQ goes live? Join our email list from the home page or follow us on social media at <a href="https://www.instagram.com/innertapapp" target="_blank" rel="noopener noreferrer">@innertapapp</a>. We'll announce updates there first.</p>
    </section>

    <section id="faq-contact" className="legal-section">
      <h2>Need Help?</h2>
      <p>Have a question you need answered now? Email <a href="mailto:support@innertap.com">support@innertap.com</a> with the subject line "FAQ" and we'll get back to you promptly.</p>
    </section>
  </>
);

export default LegalPage;
